import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'motion/react';
import { Star, MapPin, Search, Navigation, Phone, X, CheckCircle2 } from 'lucide-react';
import { Spin, Select } from 'antd';
import {reviewActions} from '../../actions/reviewActions';
import { leadActions } from '../../actions/leadActions';
import { getblobLink } from '../../services/helper';
import { Navbar } from '../../components/landingHero';
import { Footer } from '../../components/landingSections';

const StarRating = ({ rating }: { rating: number | null }) => {
    const r = rating ?? 0;
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`w-4 h-4 ${i <= Math.round(r) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
            ))}
            <span className="text-sm text-slate-500 ml-1">{r > 0 ? r.toFixed(1) : 'No rating'}</span>
        </div>
    );
};

interface InterestForm { name: string; phone: string; email: string; }

const InterestModal = ({ school, onClose }: { school: any; onClose: () => void }) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState<InterestForm>({ name: '', phone: '', email: '' });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [focused, setFocused] = useState<string | null>(null);

    const inputBase = (name: string) =>
        `w-full px-4 py-3 rounded-xl text-sm text-slate-900 bg-white border outline-none transition-all ${focused === name ? 'border-[var(--primary-color)] shadow-sm' : 'border-slate-200 hover:border-slate-300'}`;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        const payload = {
            firstName: form.name,
            email: form.email || undefined,
            phoneNumber: form.phone || undefined,
            tenantId: school.tenantId,
            isPublic: true,
        };
        dispatch(leadActions.saveLead(payload));
        setLoading(false);
        setSubmitted(true);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 transition-colors">
                    <X className="w-5 h-5" />
                </button>

                {submitted ? (
                    <div className="flex flex-col items-center text-center py-4">
                        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">Interest Registered!</h3>
                        <p className="text-slate-500 text-sm">
                            <span className="font-semibold text-[var(--primary-color)]">{school.name}</span> will reach out to you shortly.
                        </p>
                        <button onClick={onClose} className="mt-6 px-6 py-2.5 bg-[var(--primary-color)] text-white rounded-full text-sm font-semibold hover:opacity-90 transition-all">
                            Done
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="mb-6">
                            <p className="text-xs font-semibold text-[var(--primary-color)] uppercase tracking-wider mb-1">Express Interest</p>
                            <h3 className="text-xl font-bold text-slate-900">Get in touch with</h3>
                            <p className="text-lg font-semibold text-slate-700">{school.name}</p>
                            <p className="text-sm text-slate-500 mt-1">Leave your details and the school will reach out to you.</p>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-slate-700 block mb-1.5">Full Name</label>
                                <input
                                    type="text" required placeholder="Your full name"
                                    value={form.name}
                                    onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                                    onFocus={() => setFocused('name')}
                                    onBlur={() => setFocused(null)}
                                    className={inputBase('name')}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-slate-700 block mb-1.5">Phone Number</label>
                                <input
                                    type="tel" required inputMode="numeric" maxLength={10} placeholder="10-digit mobile number"
                                    value={form.phone}
                                    onChange={(e) => setForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                                    onFocus={() => setFocused('phone')}
                                    onBlur={() => setFocused(null)}
                                    className={inputBase('phone')}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-slate-700 block mb-1.5">Email Address</label>
                                <input
                                    type="email" required placeholder="you@example.com"
                                    value={form.email}
                                    onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                                    onFocus={() => setFocused('email')}
                                    onBlur={() => setFocused(null)}
                                    className={inputBase('email')}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-[var(--primary-color)] text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-all mt-2 disabled:opacity-50"
                            >
                                {loading ? <Spin size="small" /> : "I'm Interested"}
                            </button>
                        </form>
                    </>
                )}
            </motion.div>
        </div>
    );
};

// ============================================
// SCHOOL CARD (expanded)
// ============================================
const SchoolCard = ({ school, onInterest }: { school: any; onInterest: (s: any) => void }) => {
    const imgUrl = school.profileImageUrl ? school.profileImageUrl : null;
    const hasCoords = school.coordinates?.latitude && school.coordinates?.longitude;
    const isLoggedIn = Boolean(localStorage.getItem('AUTHDATA'));

    const openMap = () => {
        const { latitude, longitude } = school.coordinates;
        window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, '_blank');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-6 flex flex-col h-[240px] sm:h-[260px]"
        >
            {/* Header */}
            <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                {imgUrl ? (
                    <img src={imgUrl} alt={school.name} className="w-10 h-10 sm:w-14 sm:h-14 rounded-full object-cover border border-slate-100 flex-shrink-0" />
                ) : (
                    <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-[var(--primary-color-lighter,#EEF2FF)] flex items-center justify-center text-[var(--primary-color)] font-bold text-base sm:text-xl flex-shrink-0">
                        {school.name?.[0]?.toUpperCase() ?? 'S'}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900 text-sm sm:text-base leading-tight truncate">{school.name}</h3>
                    <StarRating rating={school.averageSchoolRating} />
                    <p className="text-xs text-slate-400 mt-0.5">{school.totalReviews} review{school.totalReviews !== 1 ? 's' : ''}</p>
                </div>
            </div>

            {/* Address */}
            <div className="flex items-start gap-2 text-xs sm:text-sm text-slate-600 flex-1 overflow-hidden">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <span className="line-clamp-2 leading-snug">
                    {school.address || school.country || 'Location not set'}
                </span>
            </div>

            {/* Phone */}
            {school.phone && (
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 mt-1 sm:mt-2">
                    <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400" />
                    <a href={`tel:${school.phone}`} className="hover:text-[var(--primary-color)] transition-colors truncate">{school.phone}</a>
                </div>
            )}

            {/* Proximity badge */}
            {school.proximityOrder > 0 && (
                <span className="inline-block text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full w-fit font-medium mt-1 sm:mt-2">
                    #{school.proximityOrder} nearby
                </span>
            )}

            {/* Actions */}
            <div className="flex gap-2 mt-2 sm:mt-3">
                {!isLoggedIn && (
                    <button
                        onClick={() => onInterest(school)}
                        className="flex-1 bg-[var(--primary-color)] text-white text-xs sm:text-sm font-semibold py-2 sm:py-2.5 rounded-xl hover:opacity-90 transition-all shadow-sm"
                    >
                        I'm Interested
                    </button>
                )}
                {hasCoords && (
                    <button
                        onClick={openMap}
                        title="Open in Google Maps"
                        className="px-3 py-2 sm:py-2.5 rounded-xl border border-[var(--primary-color)] text-[var(--primary-color)] hover:bg-[var(--primary-color)] hover:text-white transition-all flex items-center justify-center gap-1.5"
                    >
                        <Navigation className="w-4 h-4 flex-shrink-0" />
                        {isLoggedIn && <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">Directions</span>}
                    </button>
                )}
            </div>
        </motion.div>
    );
};

// ============================================
// PAGE
// ============================================
const SchoolDirectoryPage: React.FC = () => {
    const dispatch = useDispatch();
    const schoolData = useSelector((state: any) => state.getSchoolByRatingsReducer);
    const schools: any[] = schoolData?.schooldata?.data ?? [];

    const [ratingFilter, setRatingFilter] = useState<number | undefined>();
    const [coords, setCoords] = useState<{ lat: number; long: number } | null>(null);
    const [locLoading, setLocLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [interestSchool, setInterestSchool] = useState<any>(null);

    const fetchSchools = (r?: number, c?: { lat: number; long: number } | null) => {
        dispatch(reviewActions.getSchoolByRatings({
            page: 1,
            limit: 10000,
            ...(r ? { rating: r } : {}),
            ...(c ? { lat: c.lat, long: c.long } : {}),
        }));
    };

    useEffect(() => { fetchSchools(); }, []);

    const handleUseLocation = () => {
        setLocLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const c = { lat: pos.coords.latitude, long: pos.coords.longitude };
                setCoords(c);
                setLocLoading(false);
                fetchSchools(ratingFilter, c);
            },
            () => setLocLoading(false)
        );
    };

    const handleRatingChange = (val: number | undefined) => {
        setRatingFilter(val);
        fetchSchools(val, coords);
    };

    const filtered = schools.filter((s) =>
        !search || s.name?.toLowerCase().includes(search.toLowerCase()) || s.address?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-slate-50">
            <Navbar />
            <div className="pt-20 sm:pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-6 sm:mb-10">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-1 sm:mb-2">
                        All Driving <span className="text-[var(--primary-color)]">Schools</span>
                    </h1>
                    <p className="text-slate-500 text-base sm:text-lg">Find the best driving school near you.</p>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-6 sm:mb-8 items-center">
                    <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text" placeholder="Search by name or location..."
                            value={search} onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-[var(--primary-color)]"
                        />
                    </div>
                    <div className="flex gap-3 w-full sm:w-auto">
                        <Select
                            allowClear placeholder="Filter by rating"
                            style={{ flex: 1, height: 40 }}
                            value={ratingFilter} onChange={handleRatingChange}
                            options={[
                                { value: 5, label: '⭐⭐⭐⭐⭐ 5 Stars' },
                                { value: 4, label: '⭐⭐⭐⭐ 4+ Stars' },
                                { value: 3, label: '⭐⭐⭐ 3+ Stars' },
                            ]}
                        />
                        <button
                            onClick={handleUseLocation} disabled={locLoading}
                            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all whitespace-nowrap ${coords ? 'bg-[var(--primary-color)] text-white border-[var(--primary-color)]' : 'bg-white text-slate-700 border-slate-200 hover:border-[var(--primary-color)]'}`}
                        >
                            {locLoading ? <Spin size="small" /> : <MapPin className="w-4 h-4" />}
                            <span className="hidden sm:inline">{coords ? 'Location Active' : 'Near Me'}</span>
                        </button>
                        {coords && (
                            <button onClick={() => { setCoords(null); fetchSchools(ratingFilter, null); }} className="text-sm text-red-500 hover:underline whitespace-nowrap">
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Results */}
                {schoolData?.isLoading ? (
                    <div className="flex justify-center py-20"><Spin size="large" /></div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-20 text-slate-400">No schools found.</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                        {filtered.map((school: any) => (
                            <SchoolCard key={school.tenantId} school={school} onInterest={setInterestSchool} />
                        ))}
                    </div>
                )}
            </div>
            <Footer />

            <AnimatePresence>
                {interestSchool && (
                    <InterestModal school={interestSchool} onClose={() => setInterestSchool(null)} />
                )}
            </AnimatePresence>
        </div>
    );
};

export default SchoolDirectoryPage;
